import { useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const STATUSES = [
  { value: "", label: "All" }, { value: "sent", label: "Sent" }, { value: "acknowledged", label: "Acknowledged" },
  { value: "in_progress", label: "In progress" }, { value: "delivered", label: "Delivered" }, { value: "received", label: "Received" }, { value: "invoiced", label: "Invoiced" }, { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" }, { value: "draft", label: "Draft" },
];

// Every company's POs, searched and paged on the server.
export default function TransactionMonitor() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const q = useDebounced(search);
  const list = usePagedSearch("/staff/transactions/search", { q, status });
  const counts = list.counts || {};
  const txns = list.items;

  return (
    <div className="p-6 md:p-8" data-testid="transaction-monitor-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Transaction Monitor</h1>
      <p className="text-slate-500 text-sm mt-1">Cross-tenant view of purchase order activity.</p>

      <ListSearchBar value={search} onChange={setSearch} placeholder="Search PO #, contractor, vendor, requester…" testId="transactions-search" />
      <div className="flex gap-2 mt-4 flex-wrap">
        {STATUSES.map((s) => {
          const n = s.value ? counts[s.value] : counts.all;
          return (
            <button key={s.value || "all"} onClick={() => setStatus(s.value)} data-testid={`txn-filter-${s.value || "all"}`}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium ${status === s.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
              {s.label}{n ? ` (${n.toLocaleString()})` : ""}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full mt-5 text-[13px]" data-testid="transactions-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">PO #</th><th className="text-left py-2.5">Contractor</th><th className="text-left py-2.5">Vendor</th>
              <th className="text-left py-2.5">Created</th><th className="text-right py-2.5">Amount</th><th className="text-left py-2.5 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(txns || []).map((t) => (
              <tr key={t.id} className="border-b border-slate-50" data-testid={`txn-row-${t.po_number}`}>
                <td className="py-3 mono text-sky-600 font-medium">{t.po_number}</td>
                <td className="py-3">{t.company_name}</td>
                <td className="py-3 text-slate-500">{t.vendor_name}</td>
                <td className="py-3 text-slate-500">{t.created_at ? fmtDate(t.created_at) : "—"}</td>
                <td className="py-3 text-right mono font-medium">{fmt(t.amount)}</td>
                <td className="py-3 pl-3"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
            {txns && txns.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm">{q ? "No transactions match." : "No transactions yet."}</td></tr>}
            {!txns && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
      <LoadMore items={txns} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore} total={status ? counts[status] : counts.all} />
    </div>
  );
}
